import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Image as ImageIcon, Check, Lock } from "lucide-react";


// Náhľad generovaný v prehliadači (canvas) — raz sa spraví, potom je okamžitý.
const _thumbCache = new Map(); // url → dataURL (drží kým žije stránka)

function ThumbImg({ src, alt, selected }) {
  const [thumb, setThumb] = useState(() => (src && _thumbCache.get(src)) || null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!src) { setFailed(true); return; }
    if (_thumbCache.has(src)) { setThumb(_thumbCache.get(src)); return; }
    let alive = true;
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      try {
        const W = 260, H = 340;
        const c = document.createElement("canvas");
        c.width = W; c.height = H;
        const ctx = c.getContext("2d");
        // cover crop
        const ar = img.width / img.height, tar = W / H;
        let sw = img.width, sh = img.height, sx = 0, sy = 0;
        if (ar > tar) { sw = img.height * tar; sx = (img.width - sw) / 2; }
        else { sh = img.width / tar; sy = (img.height - sh) / 2; }
        ctx.drawImage(img, sx, sy, sw, sh, 0, 0, W, H);
        const data = c.toDataURL("image/jpeg", 0.7);
        _thumbCache.set(src, data);
        if (alive) setThumb(data);
      } catch (_) {
        // CORS zlyhalo pri canvas exporte → použijeme originál priamo
        _thumbCache.set(src, src);
        if (alive) setThumb(src);
      }
    };
    img.onerror = () => { if (alive) setFailed(true); };
    img.src = src;
    return () => { alive = false; };
  }, [src]);

  if (failed) {
    return (
      <div className="w-full h-full flex items-center justify-center bg-white/[0.03] text-white/25 text-[9px]">
        expired
      </div>
    );
  }
  return (
    <div className="w-full h-full bg-white/[0.04]">
      {thumb && (
        <img
          src={thumb}
          alt={alt || ""}
          className="w-full h-full object-cover transition-opacity duration-300"
          style={{ opacity: 1 }}
          draggable={false}
        />
      )}
    </div>
  );
}

export default function PhotoPicker({ fan, onSent }) {
  const [open, setOpen] = useState(false);
  const [photos, setPhotos] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [price, setPrice] = useState("9.99");
  const [teaser, setTeaser] = useState("");
  const [sending, setSending] = useState(false);
  const [page, setPage] = useState(1);
  const PER_PAGE = 12;
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/photos");
      // Fanvue fanúšikom vieme poslať len fotky nahrané vo Fanvue (majú fanvue_media_id).
      // Telegramu vieme poslať hocijakú fotku so zdrojom (URL / storage).
      const list = r.data.filter((p) => {
        if (fan?.platform === "fanvue") {
          return !!(p.fanvue_media_id || p.fanvue_uuid); // podpora oboch názvov
        }
        return !!(p.thumbnail_url || p.image_url || p.storage_path || p.url);
      });
      setPhotos(list);
      setPage(1);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      load();
      setSelected(new Set());
      setTeaser("");
      setPage(1);
    }
    // eslint-disable-next-line
  }, [open]);

  const totalPages = Math.max(1, Math.ceil(photos.length / PER_PAGE));
  const pagePhotos = photos.slice((page - 1) * PER_PAGE, page * PER_PAGE);



  const toggle = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelected(next);
    // Auto-suggest price = sum of selected
    if (next.size > 0) {
      const sum = photos
        .filter((p) => next.has(p.id))
        .reduce((s, p) => s + (parseFloat(p.price) || 0), 0);
      if (sum > 0) setPrice(sum.toFixed(2));
    }
  };

  const send = async () => {
    if (!selected.size) {
      toast.error("Vyber aspoň 1 fotku");
      return;
    }
    const priceN = parseFloat(price);
    if (!priceN || priceN < 3) {
      toast.error("Minimum cena na Fanvue je $3");
      return;
    }
    setSending(true);
    try {
      const r = await api.post("/photos/offer", {
        fan_id: fan.id,
        photo_ids: Array.from(selected),
        price: priceN,
        teaser: teaser || undefined,
      });
      toast.success(`PPV zaslané — ${selected.size} fotka(y) za $${priceN.toFixed(2)}`);
      setOpen(false);
      onSent?.(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "PPV failed");
    } finally {
      setSending(false);
    }
  };

  const isFanvue = fan?.platform === "fanvue";

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="text-[10px] uppercase tracking-widest text-fuchsia-300 hover:text-fuchsia-200 border border-fuchsia-500/30 bg-fuchsia-500/10 px-3 py-1.5 flex items-center gap-1.5"
        data-testid="btn-open-photo-picker"
        title="Send photo(s) as PPV"
      >
        <ImageIcon size={11} /> PPV
      </button>

      <Dialog open={open} onOpenChange={(v) => {
        if (!v) { setPhotos([]); setPage(1); }  // uvoľni obrázky pred animáciou zatvárania
        setOpen(v);
      }}>
        <DialogContent className="bg-[rgb(18,20,28)] border-white/10 text-white max-w-5xl w-[95vw] max-h-[92vh] overflow-hidden flex flex-col gap-2">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl flex items-center gap-3">
              Pošli PPV
              <span className="text-xs text-white/40 uppercase tracking-widest">
                pre {fan?.name}
              </span>
            </DialogTitle>
          </DialogHeader>

          {!isFanvue && (
            <div className="text-xs text-amber-300/80 bg-amber-500/10 border border-amber-500/30 px-3 py-2">
              ⚠️ Pre {fan?.platform} pošleme len text teaser (žiadny native PPV unlock). Pre reálny PPV
              odomykací flow je nutný Fanvue chat.
            </div>
          )}
          {isFanvue && photos.length === 0 && !loading && (
            <div className="text-xs text-white/50 bg-white/5 border border-white/10 px-3 py-3">
              Žiadne Fanvue fotky v trezore. Choď do <b>/vault</b> → klikni <b>Pull from Fanvue</b>.
            </div>
          )}

          {loading ? (
            <div className="grid grid-cols-3 md:grid-cols-4 gap-2 p-1 overflow-y-auto" style={{ maxHeight: "56vh" }}>
              {Array.from({ length: 12 }).map((_, i) => (
                <div key={i} className="aspect-[3/4] bg-white/[0.04] animate-pulse" />
              ))}
            </div>
          ) : photos.length === 0 ? null : (
            <>
              <div className="grid grid-cols-3 md:grid-cols-4 gap-2 p-1 overflow-y-auto content-start" style={{ maxHeight: "56vh" }}>
                {pagePhotos.map((p) => {
                  const sel = selected.has(p.id);
                  const srcFull = p.image_url || p.url || p.thumbnail_url || p.image_base64;
                  return (
                    <button
                      key={p.id}
                      onClick={() => toggle(p.id)}
                      data-testid={`pick-${p.id}`}
                      className={`relative aspect-[3/4] overflow-hidden border-2 transition-colors bg-black ${
                        sel ? "border-fuchsia-500" : "border-transparent hover:border-white/30"
                      }`}
                    >
                      <ThumbImg src={srcFull} alt={p.title} selected={sel} />
                      {sel && (
                        <div className="absolute inset-0 bg-fuchsia-500/40 flex items-center justify-center">
                          <div className="bg-fuchsia-500 rounded-full w-10 h-10 flex items-center justify-center">
                            <Check className="text-white" size={22} strokeWidth={3} />
                          </div>
                        </div>
                      )}
                      {p.nsfw && (
                        <Lock size={11} className="absolute top-1.5 left-1.5 text-rose-300 bg-black/70 rounded p-0.5" />
                      )}
                      {p.price > 0 && (
                        <div className="absolute bottom-1 right-1 text-[10px] tabular-nums font-medium text-emerald-300 bg-black/80 px-1.5 py-0.5">
                          ${p.price.toFixed(0)}
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>

              {totalPages > 1 && (
                <div className="flex items-center justify-center gap-1.5 mt-2 py-2 select-none shrink-0 border-t border-white/5">
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="px-2.5 py-1 text-sm text-white/60 hover:text-white disabled:opacity-25 disabled:cursor-default"
                  >‹</button>
                  {Array.from({ length: totalPages }).map((_, i) => (
                    <button
                      key={i}
                      onClick={() => setPage(i + 1)}
                      data-testid={`page-${i + 1}`}
                      className={`w-7 h-7 text-xs transition-colors ${
                        page === i + 1
                          ? "bg-fuchsia-500 text-white"
                          : "text-white/50 hover:text-white hover:bg-white/10"
                      }`}
                    >{i + 1}</button>
                  ))}
                  <button
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                    className="px-2.5 py-1 text-sm text-white/60 hover:text-white disabled:opacity-25 disabled:cursor-default"
                  >›</button>
                  <span className="ml-2 text-[10px] text-white/30">
                    strana {page}/{totalPages} · {photos.length} fotiek
                  </span>
                </div>
              )}
            </>
          )}

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-2 shrink-0">
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-white/40">Cena ($)</Label>
              <Input
                value={price}
                onChange={(e) => setPrice(e.target.value)}
                data-testid="input-ppv-price"
                className="bg-transparent border-white/10 rounded-none mt-1"
                type="number"
                min="3"
                step="0.01"
              />
            </div>
            <div className="md:col-span-2">
              <Label className="text-[10px] uppercase tracking-widest text-white/40">
                Text k odomknutiu (voliteľné — AI default ak prázdne)
              </Label>
              <Textarea
                value={teaser}
                onChange={(e) => setTeaser(e.target.value)}
                rows={2}
                placeholder='napr. "spravila som niečo špeciálne pre teba 💜"'
                className="bg-transparent border-white/10 rounded-none mt-1 text-sm"
                data-testid="input-ppv-teaser"
              />
            </div>
          </div>

          <div className="flex items-center justify-between mt-2 pt-2 border-t border-white/5 shrink-0">
            <div className="text-xs text-white/50">
              {selected.size} vybraných ·{" "}
              <span className="text-fuchsia-300 font-medium tabular-nums">
                ${(parseFloat(price) || 0).toFixed(2)}
              </span>
            </div>
            <Button
              onClick={send}
              disabled={sending || !selected.size}
              data-testid="btn-send-ppv"
              className="bg-fuchsia-500 hover:bg-fuchsia-400 text-white rounded-none text-xs uppercase tracking-widest px-6 py-5"
            >
              {sending ? "..." : `Poslať PPV → ${fan?.name}`}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
